import { useEffect, useState } from "react";
import logo from "../assets/images/Logo/web_logo.png";
import { NumberInput, Select, TextInput } from "../components/form/inputs";
import upldImg from "../assets/images/svgs/other/uploadImg.svg";
import UploadRow from "../components/upload/UploadRow";
import PrimaryBtn from "../components/button/PrimaryBtn";
import DecorativeSquares from "../components/ui/DecorativeSquares";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import canadaFlg from "../assets/images/svgs/country/canada.svg";
import indianFlg from "../assets/images/svgs/country/india.svg";
import usaFlg from "../assets/images/svgs/country/usa.svg";
import {
  getAvtar,
  getCountry,
  setupProfile,
  uploadPhoto,
} from "../redux/thunks/registerThunks";
import AvatarShimmer from "../components/Preloader/AvatarShimmer";
import toast from "react-hot-toast";

export default function UserProfileSetup() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [phoneTouched, setPhoneTouched] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [username, setUsername] = useState("");
  const [usernameErr, setUsernameErr] = useState("");

  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [images, setImages] = useState([]);

  const [countryCode, setCountryCode] = useState("");
  const [countryErr, setCountryErr] = useState("");

  const [contactNo, setContactNo] = useState("");
  const [imageErr, setImageErr] = useState("");

  const {
    avatars: avatarsFromApi,
    countries: countryFromApi,
    loading,
  } = useSelector((state) => state.setupProfile);

  const { user_id: reduxUserId, email: reduxEmail } = useSelector(
    (state) => state.register,
  );

  const user_id = reduxUserId || localStorage.getItem("lpCr0HsSLPU3FGP5AUbrIiMUh9lcU6ZS");
  const email = reduxEmail || localStorage.getItem("email");

  const userEmail = reduxEmail || localStorage.getItem("email");

  const COUNTRY_FLAGS = {
    99: indianFlg,
    38: canadaFlg,
    226: usaFlg,
  };

  /* ======================
     API CALLS
  ====================== */

  useEffect(() => {
    dispatch(getAvtar());
    dispatch(getCountry());
  }, [dispatch]);

  /* ======================
     AVATARS
  ====================== */

  const avatars =
    avatarsFromApi?.map((av) => ({
      id: av.id,
      img: av.image_url,
    })) || [];

  /* ======================
     COUNTRIES
  ====================== */

  const countries = countryFromApi?.map((cu) => {
    return {
      id: cu.id,
      value: cu.country_name,
      label: `${cu.country_name} (+${cu.country_code})`,
      img: COUNTRY_FLAGS[cu.id] || null,
      min_phone_length: cu.min_phone_length,
      max_phone_length: cu.max_phone_length,
    };
  });

  /* ======================
     AUTO SELECT COUNTRY
  ====================== */

  useEffect(() => {
    if (countries.length && !countryCode) {
      setCountryCode(countries[0].value);
    }
  }, [countries, countryCode]);

  const selectedCountry = countries.find((c) => c.value === countryCode);

  /* ======================
     PHONE VALIDATION
  ====================== */

  const phoneLength = contactNo.length;

  const phoneError =
    selectedCountry &&
    (phoneLength < selectedCountry.min_phone_length ||
      phoneLength > selectedCountry.max_phone_length)
      ? `Phone number must be ${selectedCountry.min_phone_length} digits`
      : "";

  /* ======================
     VALIDATIONS
  ====================== */

  const validateUserName = () => {
    if (!username.trim()) {
      return "Please enter your name";
    }
    return "";
  };

  const validateImg = () => {
    if (images.length === 0 && !selectedAvatar) {
      return "Please upload a profile image or select an avatar";
    }
    return "";
  };

  const validateCountry = () => {
    if (!countryCode) {
      return "Please select country";
    }
    return "";
  };

  /* ======================
     HANDLERS
  ====================== */

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImages([{ preview: URL.createObjectURL(file), file }]);
    setSelectedAvatar(null);
    setImageErr("");

    e.target.value = null;
  };

  const handleAvatarSelect = (avatar) => {
    setImages([]);
    setSelectedAvatar(avatar);
    setImageErr("");
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const getErrorMessage = (err) => {
    if (typeof err === "string") return err;
    if (Array.isArray(err)) return err.join(", ");
    if (err?.message) return err.message;
    return "Something went wrong";
  };

  /* ======================
     SUBMIT
  ====================== */

  const handleFinalSubmit = async () => {
    setSubmitted(true);

    const nameError = validateUserName();
    const imgError = validateImg();
    const countryError = validateCountry();

    setUsernameErr(nameError);
    setImageErr(imgError);
    setCountryErr(countryError);

    if (nameError || imgError || countryError || phoneError) return;

    try {
      let uploadedFileName = null;

      if (images.length) {
        const formData = new FormData();
        formData.append("type", "provider_profile_image");
        formData.append("image", images[0].file);

        const uploadRes = await dispatch(uploadPhoto(formData)).unwrap();
        uploadedFileName = uploadRes?.data?.temp_image_name || null;
      }

      const payload = {
        user_id: user_id,
        user_name: username || userEmail,
        user_contact_no: String(contactNo),
        country_id: selectedCountry.id,
        user_email: email,
        user_avatar_id: selectedAvatar ? selectedAvatar.id : null,
        user_profile_image: uploadedFileName,
      };

      const res = await dispatch(setupProfile(payload)).unwrap();

      toast.success(res.message || "Profile setup successful");
      navigate("/profile-created");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  /* ======================
     UI
  ====================== */

  return (
    <div className="min-h-screen relative overflow-hidden flex justify-center py-12 px-6 bg-transparent lg:bg-gradient-to-br lg:from-orange-50 lg:to-orange-100">
      <DecorativeSquares />

      <div className="w-full max-w-6xl bg-transparent lg:bg-white rounded-2xl shadow-none lg:shadow-xl p-2 lg:p-10">
        {/* LOGO */}

        <div className="flex flex-col items-center gap-2">
          <img src={logo} alt="logo" className="w-20 h-20" />
          <p className="text-gray-400 text-sm">You Cook, We Manage the Rest</p>
        </div>

        <h1 className="text-lg font-medium text-gray-700 text-center mt-10 mb-10">
          Tell us about yourself so customers can see who’s cooking!
        </h1>

        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10">
          {/* LEFT */}

          <div>
            {/* NAME */}

            <TextInput
              required
              label="Name"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (usernameErr) setUsernameErr("");
              }}
              name="username"
              error={submitted ? usernameErr : ""}
              placeholder="enter your name"
            />

            {/* CONTACT */}

            <label className="text-sm text-gray-600 mt-4 block">
              Contact Number{" "}
              <span className="text-red-500 text-sm font-bold">*</span>
            </label>

            <div className="flex gap-3 mb-1">
              <div className="w-full">
                <Select
                  required
                  name="countryCode"
                  value={countryCode}
                  onChange={(val) => {
                    setCountryCode(val);
                    if (countryErr) setCountryErr("");
                  }}
                  options={countries}
                  withImages
                  displaySelected="label"
                  error={submitted ? countryErr : ""}
                />
              </div>

              <div className="w-full">
                <NumberInput
                  placeholder="Enter mobile number"
                  value={contactNo}
                  onChange={(e) => {
                    setContactNo(e.target.value);
                    setPhoneTouched(true);
                  }}
                  maxLength={selectedCountry?.max_phone_length}
                  error={phoneTouched || submitted ? phoneError : ""}
                />
              </div>
            </div>

            {/* UPLOAD */}

            <div className="mt-6">
              <UploadRow
                id="profile-upload"
                multiple={false}
                icon={upldImg}
                text="Upload your profile photo"
                buttonText="Upload"
                onChange={handleImageUpload}
              />

              {submitted && imageErr && (
                <p className="text-xs text-red-500 mt-1">{imageErr}</p>
              )}

              <div className="grid grid-cols-4 gap-3 mt-4">
                {images.map((imgObj, i) => (
                  <div key={i} className="relative">
                    <img
                      src={imgObj.preview}
                      className="w-full h-20 object-cover rounded-xl"
                    />
                    <button
                      onClick={() => removeImage(i)}
                      className="absolute top-1 right-1 bg-black/60 text-white w-5 h-5 rounded-full"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT */}

          {images.length === 0 && (
            <div>
              <p className="text-sm text-gray-600 mb-2">
                Or Choose Your Avatar
              </p>

              {loading ? (
                <AvatarShimmer count={8} />
              ) : (
                <div className="grid grid-cols-4 gap-3 border rounded-xl p-3">
                  {avatars.map((avatar) => (
                    <div
                      key={avatar.id}
                      onClick={() => handleAvatarSelect(avatar)}
                      className="relative cursor-pointer w-20 h-20 rounded-xl overflow-hidden"
                    >
                      {selectedAvatar?.id === avatar.id && (
                        <div className="absolute top-2 left-2 bg-[#FF6943] text-white rounded-full h-5 w-5 flex items-center justify-center text-xs">
                          ✓
                        </div>
                      )}
                      <img
                        src={avatar.img}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-10 flex justify-end">
          <PrimaryBtn
            btnName="Complete Registration"
            extraClass="w-full"
            clickEvent={handleFinalSubmit}
            disabled={!!phoneError}
          />
        </div>
      </div>
    </div>
  );
}
